import { FC } from "react";
import {
  NetworkPortfolioManagementProject,
  NetworkPortfolioManagementProjectStatus,
} from "@app/network-portfolio-management/interfaces";
import { ProjectStatusBadge } from "@app/network-portfolio-management/components/ProjectStatusBadge";
import { unprocessedActionMessages } from "@app/network-portfolio-management/utils/unprocessedActionMessages";
import TooltipWrapper from "@app/shared/TooltipWrapper";

interface ProjectStatusDisplayProps {
  project: NetworkPortfolioManagementProject;
}

/**
 * Renders a project's status badge. When the backend reports Needs Attention, hovering the badge shows a tooltip
 * listing the specific unprocessed items (the flags driving it are already role-gated on the backend), mirroring
 * the Classic dashboard warning. Every other status renders a plain badge — TooltipWrapper passes children
 * through untouched when the text is empty, so no other status gains a tooltip or a tab stop.
 */
export const ProjectStatusDisplay: FC<ProjectStatusDisplayProps> = ({ project }) => {
  if (!project.status) {
    return null;
  }

  const attentionMessages = unprocessedActionMessages(project);
  const tooltipText =
    project.status === NetworkPortfolioManagementProjectStatus.NeedsAttention && attentionMessages.length > 0
      ? attentionMessages.join(" and ")
      : "";

  return (
    <TooltipWrapper text={tooltipText}>
      <span
        className="network-portfolio-management__status"
        data-testid={`network-portfolio-management-status-${project.projectId}`}
      >
        <ProjectStatusBadge status={project.status} />
      </span>
    </TooltipWrapper>
  );
};
